import type { ColumnDef } from '@tanstack/react-table';
import styled from 'styled-components';
import { IconChevronRight, IconLoader2 } from '@tabler/icons-react';

import {
    type ColumnFragmentFragment,
    type StockTreeColumnProductFragment,
    type GetSiteProductsTreeQuery,
    type StockVariationType,
    ColumnType,
} from 'data/__generated__';
import type { DataTableMeta } from 'components/DataTable/types';
import { renderAsPrice } from 'helpers/price';
import { siteVariationsColors, siteVariationsLabels } from '../constants';

const TRUNCATE_LENGTH = 50;

export const COLUMN_TYPE_LABELS: Record<ColumnType, string> = {
    [ColumnType.Warehouse]: 'Entrepôt',
    [ColumnType.ExpiredProductsWarehouse]: 'Entrepôt produits expirés',
    [ColumnType.BrokenProductsWarehouse]: 'Entrepôt produits cassés',
    [ColumnType.SecuredMicrostore]: 'Microstore sécurisé',
    [ColumnType.UnsecuredMicrostore]: 'Microstore non sécurisé',
    [ColumnType.VirtualMicrostore]: 'Microstore virtuel',
};

export type StockTreeVariation = {
    _id: string;
    createdAt: number;
    delta: number;
    type: StockVariationType;
    ref?: string | null;
    explanation?: string | null;
};

export type VariationInfo = {
    createdAt: number;
    type: string;
    variation: number;
    valeur: number;
    explication?: string | null;
    reference?: string | null;
    _bgColor: string;
};

// A node is either a column or a product. The same (column, product) leaf shape is reused in both views,
// mapped to a `product` node (view colonnes→produits) or a `column` node (view produits→colonnes).
export type StockTreeNode = {
    id: string;
    representation: 'column' | 'product';
    label: string;
    columnType: ColumnType | null;
    childCount: number | null; // root metric: nb produits (col root) / nb colonnes (product root)
    stock: number;
    targetStock: number | null;
    availableStock: { isUnlimited: boolean; value: number } | null;
    latestStockVariations: StockTreeVariation[] | null;
    merchantValuation: number;
    detailPath: string;
    hasChildren: boolean;
    // Raw ids used to lazily load children.
    columnId?: string;
    productId?: string;
    subRows?: StockTreeNode[];
};

type SiteProductTreeRow = GetSiteProductsTreeQuery['siteProducts']['siteProducts'][number];

// ----- mappers -----

export const columnToNode = (column: ColumnFragmentFragment): StockTreeNode => ({
    id: `col:${column._id}`,
    representation: 'column',
    label: column.name,
    columnType: column.type,
    childCount: column.stockSummary.productsCount,
    stock: column.stockSummary.totalStock,
    targetStock: null,
    availableStock: null,
    latestStockVariations: null,
    merchantValuation: column.stockSummary.merchantValuation,
    detailPath: `/column/${column._id}/stocks`,
    hasChildren: column.stockSummary.productsCount > 0,
    columnId: column._id,
});

// Inverse of the `productInColumnToNode` id (`col:<columnId>:prod:<productId>`). Returns the (column, product)
// pair for a product-leaf node id, or null for any other node (column roots, product roots…). Column/product
// ids are UUIDs or Meteor ids (no colons), so the split is unambiguous.
export const parseColumnProductNodeId = (nodeId: string): { columnId: string; productId: string } | null => {
    const match = nodeId.match(/^col:(.+):prod:(.+)$/);
    return match ? { columnId: match[1], productId: match[2] } : null;
};

export const productInColumnToNode = (columnProduct: StockTreeColumnProductFragment): StockTreeNode => ({
    id: `col:${columnProduct.columnId}:prod:${columnProduct.productId}`,
    representation: 'product',
    label: columnProduct.product.fullname,
    columnType: null,
    childCount: null,
    stock: columnProduct.stock,
    targetStock: columnProduct.targetStock,
    availableStock: columnProduct.availableStock,
    latestStockVariations: columnProduct.latestStockVariations,
    merchantValuation: columnProduct.merchantValuation,
    detailPath: `/column/${columnProduct.columnId}/stocks/${columnProduct.productId}`,
    hasChildren: false,
    productId: columnProduct.productId,
});

export const siteProductToNode = (siteProduct: SiteProductTreeRow): StockTreeNode => ({
    id: `prod:${siteProduct.productId}`,
    representation: 'product',
    label: siteProduct.product.fullname,
    columnType: null,
    childCount: siteProduct.columnsCount,
    stock: siteProduct.stock,
    targetStock: siteProduct.targetStock,
    availableStock: siteProduct.availableStock,
    latestStockVariations: siteProduct.latestStockVariations,
    merchantValuation: siteProduct.merchantValuation,
    detailPath: `/product/${siteProduct.productId}`,
    hasChildren: siteProduct.columnsCount > 0,
    productId: siteProduct.productId,
});

export const columnForProductToNode = (columnProduct: StockTreeColumnProductFragment): StockTreeNode => ({
    id: `prod:${columnProduct.productId}:col:${columnProduct.columnId}`,
    representation: 'column',
    label: columnProduct.column.name,
    columnType: columnProduct.column.type,
    childCount: null,
    stock: columnProduct.stock,
    targetStock: columnProduct.targetStock,
    availableStock: columnProduct.availableStock,
    latestStockVariations: columnProduct.latestStockVariations,
    merchantValuation: columnProduct.merchantValuation,
    detailPath: `/column/${columnProduct.columnId}/stocks/${columnProduct.productId}`,
    hasChildren: false,
    columnId: columnProduct.columnId,
});

// Columns view + productName filter: group the matching (column, product) pairs by column → one column root
// (deduplicated) per column, pre-expanded onto its matching products. Root metrics aggregate the matches.
export const buildColumnsWithMatchingProducts = (columnStocks: StockTreeColumnProductFragment[]): StockTreeNode[] => {
    const byColumnId = new Map<string, StockTreeColumnProductFragment[]>();
    for (const columnStock of columnStocks) {
        const entries = byColumnId.get(columnStock.columnId) ?? [];
        entries.push(columnStock);
        byColumnId.set(columnStock.columnId, entries);
    }

    return [...byColumnId.values()].map((products) => {
        const { columnId, column } = products[0];
        return {
            id: `col:${columnId}`,
            representation: 'column',
            label: column.name,
            columnType: column.type,
            childCount: products.length,
            stock: products.reduce((total, product) => total + product.stock, 0),
            targetStock: null,
            availableStock: null,
            latestStockVariations: null,
            merchantValuation: products.reduce((total, product) => total + product.merchantValuation, 0),
            detailPath: `/column/${columnId}/stocks`,
            hasChildren: true,
            columnId,
            subRows: products.map(productInColumnToNode),
        };
    });
};

// ----- columns -----

type StockTreeColumnsConfig = {
    setInfos: (info: VariationInfo | null) => void;
};

export const getStockTreeColumns = ({ setInfos }: StockTreeColumnsConfig): ColumnDef<StockTreeNode, unknown>[] => [
    {
        id: 'label',
        accessorFn: (row) => row.label,
        header: 'Désignation',
        cell: ({ row, table }) => {
            const meta = table.options.meta as DataTableMeta | undefined;
            const isLoading = meta?.loadingRowIds?.has(row.id) ?? false;
            const { label } = row.original;
            const truncated = label.length > TRUNCATE_LENGTH ? `${label.slice(0, TRUNCATE_LENGTH)}…` : label;

            return (
                <LabelCell style={{ paddingLeft: `${row.depth * 22}px` }}>
                    {row.getCanExpand() ? (
                        <ExpandButton
                            type="button"
                            onClick={(event) => {
                                event.stopPropagation();
                                row.getToggleExpandedHandler()();
                            }}
                            data-test={`stock-tree-expand-${row.id}`}
                        >
                            {isLoading ? (
                                <IconLoader2 size={16} className="animate-spin" />
                            ) : (
                                <Chevron size={16} $expanded={row.getIsExpanded()} />
                            )}
                        </ExpandButton>
                    ) : (
                        <ExpandSpacer />
                    )}
                    <LabelText $bold={row.depth === 0} title={label.length > TRUNCATE_LENGTH ? label : undefined}>
                        {truncated}
                    </LabelText>
                </LabelCell>
            );
        },
    },
    {
        id: 'type',
        accessorFn: (row) => (row.columnType ? COLUMN_TYPE_LABELS[row.columnType] : ''),
        header: 'Type',
        enableGlobalFilter: false,
        cell: ({ row }) => <span>{row.original.columnType ? COLUMN_TYPE_LABELS[row.original.columnType] : '—'}</span>,
    },
    {
        id: 'variations',
        header: 'Variations de stock',
        enableSorting: false,
        enableGlobalFilter: false,
        cell: ({ row }) => {
            const variations = row.original.latestStockVariations;
            if (!variations || variations.length === 0) return <span>—</span>;

            let value = row.original.stock;

            return (
                <VariationsContainer>
                    {variations.map((variation) => {
                        value = value + variation.delta;
                        const bgColor = siteVariationsColors[variation.type];
                        const infos: VariationInfo = {
                            createdAt: variation.createdAt,
                            type: siteVariationsLabels[variation.type],
                            variation: variation.delta,
                            valeur: value,
                            explication: variation.explanation,
                            reference: variation.ref,
                            _bgColor: bgColor,
                        };

                        return (
                            <VariationBadgeContainer
                                key={variation._id}
                                onMouseEnter={() => setInfos(infos)}
                                onMouseLeave={() => setInfos(null)}
                            >
                                <VariationBadge $bgColor={bgColor}>{variation.delta}</VariationBadge>
                            </VariationBadgeContainer>
                        );
                    })}
                </VariationsContainer>
            );
        },
    },
    {
        id: 'childCount',
        accessorFn: (row) => row.childCount ?? -1,
        header: 'Nb. Ref.',
        enableGlobalFilter: false,
        cell: ({ row }) => <span>{row.original.childCount ?? '—'}</span>,
    },
    {
        id: 'stock',
        accessorFn: (row) => row.stock,
        header: 'Stock actuel',
        enableGlobalFilter: false,
        cell: ({ row }) => <span>{row.original.stock}</span>,
    },
    {
        id: 'targetStock',
        accessorFn: (row) => row.targetStock ?? -1,
        header: 'Stock cible',
        enableGlobalFilter: false,
        cell: ({ row }) => <span>{row.original.targetStock ?? '—'}</span>,
    },
    {
        id: 'availableStock',
        accessorFn: (row) =>
            row.availableStock ? (row.availableStock.isUnlimited ? Infinity : row.availableStock.value) : -1,
        header: 'Stock disponible',
        enableGlobalFilter: false,
        cell: ({ row }) => {
            const availableStock = row.original.availableStock;
            if (!availableStock) return <span>—</span>;
            return <span>{availableStock.isUnlimited ? '∞' : availableStock.value}</span>;
        },
    },
    {
        id: 'merchantValuation',
        accessorFn: (row) => row.merchantValuation,
        header: 'Valorisation marchande (en €HT)',
        enableGlobalFilter: false,
        cell: ({ row }) => <span>{renderAsPrice(row.original.merchantValuation)}</span>,
    },
];

const LabelCell = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`;

const ExpandButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    padding: 0;
    border: none;
    background: transparent;
    cursor: pointer;
    color: ${({ theme }) => theme.textColor};
`;

const ExpandSpacer = styled.span`
    display: inline-block;
    width: 20px;
`;

const Chevron = styled(IconChevronRight)<{ $expanded: boolean }>`
    transition: transform 0.15s ease;
    transform: rotate(${({ $expanded }) => ($expanded ? '90deg' : '0deg')});
`;

const LabelText = styled.span<{ $bold: boolean }>`
    font-weight: ${({ $bold }) => ($bold ? 600 : 400)};
`;

const VariationsContainer = styled.div`
    display: inline-block;
    overflow: auto;
    max-width: 150px;
`;

const VariationBadgeContainer = styled.div`
    display: inline;
`;

const VariationBadge = styled.span<{ $bgColor?: string }>`
    cursor: pointer;
    margin-right: 10px;
    background-color: ${({ $bgColor }) => $bgColor};
    color: ${({ $bgColor }) => ($bgColor === '#FFFFFF' ? '#111111' : '#FFFFFF')};
    border-radius: 3px;
    padding: 5px;
`;
